import React from "react";
import { Chip } from "@mui/material";

export const REPORT_TYPES = [
    { value: "user_actions", label: "User Actions" },
    { value: "inbound_messages", label: "Inbound Messages" },
    { value: "message_status_events", label: "Message Events - Raw" },
    { value: "message_events_three_failed", label: "Message Events Report 3 Failed" },
    { value: "facility_general", label: "Facility / General" },
    { value: "outgoing_message_templates", label: "Outgoing Message Templates" },
];

export const REPORT_ACTIONS = [
    { value: "initiated", label: "Initiated" },
    { value: "signed_initiations", label: "Signed Initiations" },
    { value: "verbal", label: "Verbal" },
    { value: "upload_admission", label: "Upload Admission" },
];

export const DATE_RANGES = [
    { value: "1_day", label: "1 Day" },
    { value: "7_days", label: "7 Days" },
    { value: "2_weeks", label: "2 Weeks" },
    { value: "month", label: "Current Month" },
    { value: "custom", label: "Custom" },
];

export const DATE_COLUMNS = [
    { value: "created_on", label: "Created Date" },
    { value: "effective_date", label: "Effective Date" },
];

export const MESSAGE_STATUS_FILTERS = [
    { value: "undelivered", label: "Undelivered" },
    { value: "delivered", label: "Delivered" },
    { value: "", label: "Delivered + Undelivered" },
];

export const MESSAGE_LIMITS = [25, 50, 100, 200];

export const ACTIONS_WITH_CONSENT_BREAKDOWN = new Set([
    "signed_initiations",
    "verbal",
    "upload_admission",
]);

export function formatDateForApi(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function getPresetDateRange(range) {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const start = new Date(today);
    const stop = new Date(today);

    switch (range) {
        case "7_days":
            start.setDate(today.getDate() - 6);
            break;
        case "2_weeks":
            start.setDate(today.getDate() - 13);
            break;
        case "month":
            start.setFullYear(today.getFullYear(), today.getMonth(), 1);
            stop.setFullYear(today.getFullYear(), today.getMonth() + 1, 0);
            break;
        default:
            break;
    }

    return { start: formatDateForApi(start), stop: formatDateForApi(stop) };
}

export function getEmployeeDisplayName(employee) {
    const employeeName = String(employee?.employeeName ?? "").trim();
    if (employeeName) return employeeName;
    const givenName = String(employee?.givenName ?? "").trim();
    const familyName = String(employee?.familyName ?? "").trim();
    return `${givenName} ${familyName}`.trim() || "Unknown user";
}

export function getUserDisplayName(user) {
    const firstName = String(user?.first_name ?? "").trim();
    const lastName = String(user?.last_name ?? "").trim();
    const email = String(user?.email ?? "").trim();
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName ? `${fullName} (${email})` : email;
}

export function getErrorMessage(error, fallbackMessage) {
    return error instanceof Error && error.message ? error.message : fallbackMessage;
}

export function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export function formatDateOnly(value) {
    if (!value) return "";
    const raw = String(value).trim();
    return raw ? raw.split("T")[0].split(" ")[0] : "";
}

export function formatPhone(value) {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    const digits = raw.replace(/\D/g, "");
    const tenDigits = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    return tenDigits.length === 10
        ? `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`
        : raw;
}

export function ConsentStatusChip({ status }) {
    const value = String(status ?? "").trim();
    if (!value) return <Chip size="small" label="Unknown" variant="outlined" />;
    let color = "default";
    if (value.toLowerCase() === "opted in") color = "success";
    else if (value.toLowerCase() === "opted out") color = "error";
    else if (value.toLowerCase() === "pending") color = "warning";
    return <Chip size="small" label={value} color={color} variant={color === "default" ? "outlined" : "filled"} />;
}

export function MessageStatusChip({ status }) {
    const value = String(status ?? "").trim().toLowerCase();
    let color = "default";
    if (value === "delivered") color = "success";
    else if (value === "undelivered" || value === "failed") color = "error";
    else if (value === "sent") color = "info";
    else if (value === "queued" || value === "accepted") color = "warning";
    return <Chip size="small" label={value || "Unknown"} color={color} variant={color === "default" ? "outlined" : "filled"} />;
}

export function AccountStatusChip({ status }) {
    const clean = String(status || "").trim();
    const inactive = clean.toLowerCase() === "inactive";
    return (
        <Chip
            size="small"
            label={clean || "Unknown"}
            sx={{
                height: "20px",
                fontSize: "11px",
                fontWeight: 700,
                backgroundColor: inactive ? "#d32f2f" : "#e8f5e9",
                color: inactive ? "#fff" : "#2e7d32",
            }}
        />
    );
}
